/**
 * Italian IBAN validators / formatters.
 *
 * Pure utility functions — no reactive state. Components import the
 * named functions directly. Used by the AccountPanelIban form for
 * real-time validation hint and submit gating.
 *
 * The format check accepts the canonical Italian IBAN structure:
 *   IT + 2 check digits + CIN(1 letter) + ABI(5 digits) + CAB(5
 *   digits) + 12-char account = 27 characters total. Spaces are
 *   stripped before checking; lowercase is uppercased. The check
 *   does NOT validate the ISO/IEC 7064 mod-97 digits — backend
 *   does that authoritatively.
 */

/** Strip whitespace and uppercase so display and validation align. */
export function normalizeIban(s: string): string {
  return (s || '').replace(/\s+/g, '').toUpperCase()
}

/**
 * Italian IBAN structural check (length + character classes). Use
 * after `normalizeIban`. Backend remains the authoritative validator
 * — this is only good enough to drive the form's hint colour and
 * submit-disabled state.
 */
export function isValidItalianIban(s: string): boolean {
  return /^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/.test(normalizeIban(s))
}

/** Group an IBAN into 4-char chunks for display: `IT60 X054 ...`. */
export function formatIban(s: string): string {
  return normalizeIban(s)
    .replace(/(.{4})/g, '$1 ')
    .trim()
}
